import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, CreditCard, Users, BellRing, Inbox, Download,
  Wallet, ShieldCheck, Sparkles,
} from "lucide-react";
import PublicLayout from "../../components/PublicLayout";
import { Button } from "../../components/ui/button";
import { useBrand } from "../../lib/BrandContext";

const FEATURES = [
  { icon: Users,        title: "Build your roster",       body: "Invite parents by email or link. They fill in the rest." },
  { icon: CreditCard,   title: "Create fees fast",        body: "Monthly dues, uniforms, tournaments, fundraisers, or custom fees." },
  { icon: CheckCircle2, title: "Track every status",      body: "See who&#39;s unpaid, waiting for review, paid, or waived at a glance." },
  { icon: BellRing,     title: "Send reminders",          body: "Nudge one parent, a group, or everyone still unpaid." },
  { icon: Inbox,        title: "Review submissions",      body: "Parents mark payments as sent. You confirm or ask them to resubmit." },
  { icon: Download,     title: "Export records",          body: "Download clean CSV reports for your team or organization." },
];

export default function Home() {
  const brand = useBrand();
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="container-page grid gap-12 py-16 md:grid-cols-2 md:py-24 lg:py-28">
          <div className="flex flex-col justify-center">
            <span
              className="inline-flex w-max items-center gap-2 rounded-full border border-brand-100 bg-brand-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand"
              data-testid="hero-badge"
            >
              <Sparkles className="h-3.5 w-3.5" /> For coaches, clubs &amp; team organizers
            </span>
            <h1 className="h1 mt-5" data-testid="hero-headline">
              Stop chasing dues. <br />
              <span className="text-brand">Start coaching.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-ink-700" data-testid="hero-subheadline">
              {brand.app_name} helps coaches track who owes, who paid, and who still needs a reminder —
              without processing a single card.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/signup" data-testid="hero-start-free">
                <Button className="h-12 w-full bg-brand px-7 font-semibold text-white hover:bg-brand-hover sm:w-auto">
                  Start free <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-see-pricing">
                <Button variant="outline" className="h-12 w-full border-ink-300 font-semibold sm:w-auto">
                  See pricing
                </Button>
              </Link>
            </div>
            <p className="mt-3 text-xs text-ink-500" data-testid="hero-trial-note">
              {brand.free_early_access ? "Free during Early Access. No credit card required." : "14-day free trial. No credit card required."}
            </p>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-ink-100 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1770237710424-7ffa780f68d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjb2FjaCUyMHdpdGglMjB0ZWFtJTIwb3V0ZG9vcnN8ZW58MHx8fHwxNzc3MzQxNjA2fDA&ixlib=rb-4.1.0&q=85"
                alt="Coach with their team outdoors"
                className="h-full w-full object-cover"
              />
            </div>
            <div
              className="absolute -bottom-6 -left-6 hidden w-64 rounded-2xl border border-ink-100 bg-white p-5 shadow-lg sm:block"
              data-testid="hero-stats-card"
            >
              <p className="label-caps">Confirmed this month</p>
              <p className="font-heading text-3xl font-bold text-ink-900">$4,820.00</p>
              <p className="mt-1 text-sm text-emerald-600">+18% vs. last month</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-t border-ink-100 bg-ink-100/30 py-16">
        <div className="container-page">
          <p className="label-caps">What you get</p>
          <h2 className="h2 mt-2 max-w-2xl" data-testid="features-headline">
            Everything a coach needs to collect without the chaos.
          </h2>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-xl border border-ink-100 bg-white p-6 shadow-sm card-hover"
                data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="h3 mt-4">{title}</h3>
                <p className="mt-2 text-sm text-ink-500">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parents section */}
      <section className="py-16">
        <div className="container-page grid gap-10 md:grid-cols-2 md:items-center">
          <div>
            <div className="overflow-hidden rounded-2xl border border-ink-100 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1758687125923-26d704358dc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwYXJlbnQlMjB3YXRjaGluZyUyMGtpZHMlMjBzcG9ydHN8ZW58MHx8fHwxNzc3MzQxNjA3fDA&ixlib=rb-4.1.0&q=85"
                alt="Parent watching kids play soccer"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div>
            <p className="label-caps">Built for parents too</p>
            <h2 className="h2 mt-2" data-testid="parents-headline">
              Parents pay how they already do.
            </h2>
            <p className="mt-4 text-lg text-ink-700" data-testid="parents-body">
              Venmo, Zelle, Cash App, cash, check, or other. {brand.app_name} simply tracks the payment status
              so coaches stay organized.
            </p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="payment-methods-chips">
              {["Venmo", "Zelle", "Cash App", "Cash", "Check", "Other"].map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center gap-1 rounded-full border border-ink-200 bg-white px-3 py-1 text-xs font-semibold text-ink-700"
                >
                  <Wallet className="h-3 w-3 text-brand" /> {m}
                </span>
              ))}
            </div>
            <div className="mt-6 rounded-xl border border-ink-100 bg-white p-5">
              <p className="font-heading text-lg font-semibold text-ink-900">
                “Ninety minutes a week back in my life. That’s not a feature, that’s a gift.”
              </p>
              <p className="mt-3 text-sm text-ink-500">— Coach Maria R., Westside Soccer Club</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-ink-100 bg-ink-900 py-20 text-white">
        <div className="container-page text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-brand" />
          <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">Get your weekends back.</h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-300" data-testid="footer-cta-note">
            {brand.free_early_access ? brand.early_access_notice : "14-day free trial. No credit card required."}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/signup" data-testid="footer-cta-signup">
              <Button className="h-12 bg-brand px-7 font-semibold text-white hover:bg-brand-hover">Start free</Button>
            </Link>
            <Link to="/pricing" data-testid="footer-cta-pricing">
              <Button variant="outline" className="h-12 border-ink-700 bg-transparent px-7 font-semibold text-white hover:bg-ink-700">See pricing</Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
